import { useCallback, useEffect, useState } from 'react'

import { getKpiGoals } from '@/api/kpi'
import { ClarityKpiGoalResponseModel, KpiGoalsModel } from '@/types'

// Define the fields for each group based on KpiGoalsModel
const KPI_GROUP_FIELDS: Record<keyof KpiGoalsModel, string[]> = {
  dashboard: ['googleReviews'],
  sales: ['adns', 'traffic', 'aov', 'discountPercentage'],
  spend: [
    'cogsPercentage',
    'ptiImpact',
    'compliancePercentage',
    'cogsVsTheoretical',
    'inventoryValue',
    'totalSpend',
  ],
  labor: [
    'laborRatio',
    'laborHours',
    'wagePerHour',
    'tipPercentage',
    'overtimePercentage',
  ],
  speedOfService: [
    'speedOfService',
    'utilization',
    'serviceTime',
    'windowTime',
  ],
  loyalty: [
    'loyaltyPercentage',
    'newUserPercentage',
    'frequency',
    'retentionPercentage',
    'discountPenetrationPercentage',
  ],
}

const toSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

// Find value from response - tries camelCase and snake_case variations
const findValue = (
  response: ClarityKpiGoalResponseModel,
  fieldName: string
): number | null => {
  const snakeCaseField = toSnakeCase(fieldName)

  // Try exact match first (camelCase), then snake_case
  if (fieldName in response && response[fieldName] !== undefined) {
    return response[fieldName]
  }
  if (snakeCaseField in response && response[snakeCaseField] !== undefined) {
    return response[snakeCaseField]
  }

  return null
}

// Normalize group name for matching
const normalizeGroupName = (name: string): string =>
  name.toLowerCase().replace(/[_\s-]/g, '')

// Get the actual group key from KpiGoalsModel based on kpiGroupName
const getGroupKey = (kpiGroupName: string): keyof KpiGoalsModel | null => {
  const normalized = normalizeGroupName(kpiGroupName)
  const groupKeys = Object.keys(KPI_GROUP_FIELDS) as (keyof KpiGoalsModel)[]

  return groupKeys.find((key) => normalizeGroupName(key) === normalized) ?? null
}

// Maps API response keys to KpiGoalsModel structure based on kpiGroupName
const mapResponseToKpiGoals = (
  response: ClarityKpiGoalResponseModel,
  kpiGroupName: string
): KpiGoalsModel => {
  const groupKey = getGroupKey(kpiGroupName)

  if (!groupKey) {
    return {}
  }

  const fields = KPI_GROUP_FIELDS[groupKey]
  const groupData: Record<string, number | null | undefined> = {}

  for (const field of fields) {
    groupData[field] = findValue(response, field)
  }

  return {
    [groupKey]: groupData,
  } as KpiGoalsModel
}

const mapKpiGroupName: Record<string, string> = {
  sales: 'sales',
  labor: 'labor',
  'speed-of-service': 'speed_of_service',
  loyalty: 'loyalty',
  'profit-and-loss': 'profit_and_loss',
  spend: 'spend',
}

export default function useKpiGoals(
  startDate: string,
  endDate: string,
  kpiGroupName: string,
  branches: string,
  tenant: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [kpiGoals, setKpiGoals] = useState<KpiGoalsModel | null>(null)

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    if (tenant === 'blankstreet') {
      setKpiGoals(null)
      setLoading(false)
      return
    }

    if (kpiGroupName === 'profit-and-loss') {
      setKpiGoals(null)
      setLoading(false)
      return
    }

    try {
      if (!kpiGroupName || !branches) {
        setKpiGoals(null)
        throw new Error('KPI group name and branches are required')
      }

      let mappedKpiGoals: KpiGoalsModel | null = null
      const mappedKpiGroupName = mapKpiGroupName[kpiGroupName]

      const response = await getKpiGoals(
        startDate,
        endDate,
        mappedKpiGroupName,
        branches
      )

      // Map response to KpiGoalsModel if it's a ClarityKpiGoalResponseModel
      if (response && !('branches' in response)) {
        mappedKpiGoals = mapResponseToKpiGoals(
          response as ClarityKpiGoalResponseModel,
          kpiGroupName
        )
      }

      setKpiGoals(mappedKpiGoals)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred'
      setError(new Error(errorMessage))
      setKpiGoals(null)
    } finally {
      setLoading(false)
    }
  }, [branches, kpiGroupName, startDate, endDate, tenant])

  const refetch = useCallback(() => {
    console.log('Refetching KPI goals data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    kpiGoals,
    loading,
    error,
    refetch,
  }
}
